import { useQuery } from '@tanstack/react-query';

import axios, { parseAxiosError } from '@/portainer/services/axios/axios';

import { queryKeys } from './query-keys';

/**
 * Base parameters for Git Helm chart operations
 */
export type GitHelmChartBaseParams = {
  /** The Git repository URL */
  repository: string;
  /** The path to the chart within the repository */
  chartPath: string;
  /** The Git reference (branch/tag) */
  reference?: string;
  /** The username for Git authentication */
  username?: string;
  /** The password for Git authentication */
  password?: string;
  /** The authorization type for Git authentication */
  authorizationType?: number;
  /** The Git credential ID to use */
  gitCredentialID?: number;
  /** Whether to skip TLS verification */
  tlsSkipVerify?: boolean;
};

type GitHelmChartParams = GitHelmChartBaseParams & {
  /** The command to run (chart, values, readme) */
  command?: 'chart' | 'values' | 'readme';
};

type GitFilePreviewResponse = {
  FileContent: string;
};

export function useGitHelmChartValues(
  params: GitHelmChartParams,
  enabled = true
) {
  const hasValidGitParams = !!params.repository && !!params.command;

  return useQuery({
    queryKey: queryKeys.gitChartValues(
      params.repository,
      params.chartPath,
      params.reference || 'refs/heads/main',
      params.command || 'values'
    ),
    queryFn: async () => getGitHelmChartValues(params),
    enabled: hasValidGitParams && enabled,
    select: (data) => ({
      values: data,
    }),
    retry: false,
  });
}

/**
 * Gets Helm chart files (values.yaml, Chart.yaml, README.md) from a Git repository
 * using the existing Git file preview endpoint.
 *
 * This is simpler and more efficient than calling helm CLI because:
 * - Helm's show commands just read specific files from the chart directory
 * - values.yaml is always at <chart-path>/values.yaml
 * - Chart.yaml is always at <chart-path>/Chart.yaml
 * - README.md is always at <chart-path>/README.md
 */
async function getGitHelmChartValues(params: GitHelmChartParams) {
  try {
    const {
      repository,
      chartPath,
      reference = 'refs/heads/main',
      username,
      password,
      authorizationType = 0,
      gitCredentialID = 0,
      tlsSkipVerify = false,
      command = 'values',
    } = params;

    // Map command to file name (following Helm's convention)
    const fileMap = {
      values: 'values.yaml',
      chart: 'Chart.yaml',
      readme: 'README.md',
    };

    // Construct the full file path: chartPath + filename
    const targetFile = chartPath
      ? `${chartPath}/${fileMap[command]}`
      : fileMap[command];

    const payload = {
      repository,
      targetFile,
      reference,
      username,
      password,
      authorizationType,
      gitCredentialID,
      tlsSkipVerify,
    };

    // Use the existing Git file preview endpoint
    const response = await axios.post<GitFilePreviewResponse>(
      '/gitops/repo/file/preview',
      payload
    );

    return response.data.FileContent;
  } catch (err) {
    throw parseAxiosError(
      err,
      'Unable to get Helm chart file from Git repository'
    );
  }
}
